import { isBefore } from 'date-fns';
import React, { useMemo } from 'react';
import * as Yup from 'yup';

import { PortfolioSettlementRule, SettlementType } from '@ndvr/billing-api';

import { parseLocalDate, todayDate, tomorrowDate, useRequiredParams } from 'utils';
import { FormSection, requiredStringValidator } from 'widgets';
import { FormDateInput } from 'widgets/form/input/FormDateInput';

import { usePortfolioSettlementRuleTimeline, useSavePortfolioSettlementRule } from '../billing-queries';
import { getDisabledDates, getFirstAvailableDate, isDateDisabled } from '../billing-utils';
import { PortfolioSelect } from '../widgets/PortfolioSelect';
import { SettlementSelect } from '../widgets/SettlementSelect';
import { BillingDrawer } from './BillingDrawer';

interface Props {
    portfolioSettlementRule?: PortfolioSettlementRule;
    onClose: () => void;
}

export const SettlementOverrideDrawer = ({ portfolioSettlementRule, onClose }: Props) => {
    const { globalAccountGUID } = useRequiredParams(['globalAccountGUID']);
    const { mutate: savePortfolioSettlementRule, isPending } = useSavePortfolioSettlementRule(
        globalAccountGUID,
        !!portfolioSettlementRule,
    );

    const { data: timeline } = usePortfolioSettlementRuleTimeline(globalAccountGUID);
    const disabledDates = getDisabledDates(
        timeline?.upcomingRules.filter(
            (rule) => rule.portfolioSettlementRuleGUID !== portfolioSettlementRule?.portfolioSettlementRuleGUID,
        ),
    );
    const firstAvailableDate = getFirstAvailableDate(disabledDates);

    const defaultValues = useMemo(() => {
        return portfolioSettlementRule
            ? { ...portfolioSettlementRule }
            : ({
                  globalAccountGUID,
                  settlementType: SettlementType.DEFAULT_SETTLEMENT,
                  startDate: firstAvailableDate,
              } as PortfolioSettlementRule);
    }, [firstAvailableDate, globalAccountGUID, portfolioSettlementRule]);

    const formSchema = Yup.object({
        portfolioGUID: requiredStringValidator.label('Account'),
        settlementType: requiredStringValidator,
        portfolioForSettlement: Yup.string().when('settlementType', {
            is: (type: SettlementType) =>
                type === SettlementType.SETTLED_BY_PORTFOLIO || type === SettlementType.SETTLED_BY_EXTERNAL_PORTFOLIO,
            then: () => requiredStringValidator.label('Account for settlement'),
        }),
        globalAccountForSettlement: Yup.string().when('settlementType', {
            is: SettlementType.SETTLED_BY_EXTERNAL_PORTFOLIO,
            then: () => requiredStringValidator.label('Household for settlement'),
        }),
        startDate: requiredStringValidator.test(
            'start-date-available',
            'Date already within an override interval',
            (startDate) => !isDateDisabled(parseLocalDate(startDate), disabledDates),
        ),
        endDate: Yup.string()
            .test(
                'end-date-available',
                'Date already within an override interval',
                (endDate) => !endDate || !isDateDisabled(parseLocalDate(endDate), disabledDates),
            )
            .test(
                'date-overlap',
                'Date range would overlap with existing one',
                (endDate, ctx) =>
                    !!endDate ||
                    disabledDates.every((disabledDate) => isBefore(disabledDate, parseLocalDate(ctx.parent.startDate))),
            ),
    });

    const editingActiveRule = useMemo(() => {
        if (!portfolioSettlementRule) {
            return false;
        }
        return parseLocalDate(portfolioSettlementRule.startDate) <= todayDate();
    }, [portfolioSettlementRule]);

    return (
        <BillingDrawer
            title={portfolioSettlementRule ? 'Update Settlement Override' : 'Create New Settlement Override'}
            editing={!!portfolioSettlementRule}
            formSchema={formSchema}
            defaultValues={defaultValues}
            onSubmit={savePortfolioSettlementRule}
            pending={isPending}
            onClose={onClose}
        >
            <FormSection>
                <PortfolioSelect
                    name="portfolioGUID"
                    globalAccountGUID={globalAccountGUID}
                    disabled={!!portfolioSettlementRule}
                />
            </FormSection>
            <FormSection hasDivider>
                <SettlementSelect
                    title="AUM Charge"
                    names={{ type: 'settlementType', settlementPortfolioGUID: 'portfolioForSettlement' }}
                    globalAccountGUID={globalAccountGUID}
                    defaultSettlementDescription="this account"
                    disabled={editingActiveRule}
                />
            </FormSection>
            <FormSection hasDivider>
                <FormDateInput
                    name="startDate"
                    label="Start Date"
                    minDate={tomorrowDate()}
                    disabled={editingActiveRule}
                    disabledDates={disabledDates}
                />
                <FormDateInput
                    name="endDate"
                    label="End Date"
                    minDate={editingActiveRule ? todayDate() : tomorrowDate()}
                    disabledDates={disabledDates}
                />
            </FormSection>
        </BillingDrawer>
    );
};
